/*
 * Event emitter module. Can be used as a standalone factory or as a mixin.
 *
 * @private
 * @class emitter
 * @example
 * ``` javascript
 * var eventEmitter = emitter(); // Create a new emitter.
 * emitter(myEmittingObject.prototype); // Mixin to an existing object.
 * ```
 */
export default function eventEmitter (prototype = {}) {
  var eventMap = []
  var subscribeMap = []
  var strictMode = false

  /*
   * Check if the event is a valid event type.
   */
  function checkEvent (type) {
    if (strictMode && !eventMap[type]) {
      throw new Error('Invalid event type: ' + type)
    }
  }

  return Object.assign(prototype, {
    /*
     * Define an event type with the event emitter.
     *
     * @method define
     * @param {String} type The name for the event type.
     */
    define: function (type) {
      eventMap[type] = eventMap[type] || []
    },

    /*
     * Define an alias for an event type.
     *
     * @method alias
     * @param {String} type The event type for which to add an alias.
     * @param {String} alias The alias name for the event type.
     * @throws {Error} Invalid event type
     */
    alias: function (type, alias) {
      checkEvent(type)

      eventMap[alias] = eventMap[type] = eventMap[type] || []
    },

    /*
     * Add an event listener for the specified event type.
     *
     * @method on
     * @param {String} type The event type for which to add the listener.
     * @param {Function} listener The listener for the event type. The parameters
     *                            of the listener depend on the event type.
     * @throws {Error} Invalid event type
     */
    on: function (type, listener) {
      checkEvent(type)
      ;(eventMap[type] = eventMap[type] || []).push(listener)
    },

    /*
     * Removes an event listener for the specified event type.
     *
     * @method off
     * @param {String} type The event type for which to remote the listener.
     * @param {Function} listener The listener to remove.
     * @throws {Error} Invalid event type
     */
    off: function (type, listener) {
      checkEvent(type)

      var list = eventMap[type] || []
      var i = list.length
      while (i--) {
        if (listener === list[i]) {
          list.splice(i, 1)
        }
      }
    },

    /*
     * Emits an event of the specified type.
     *
     * @method emit
     * @param {String} type The event type to emit.
     * @param {any} [...args] The arguments to pass to the listeners of the event.
     * @throws {Error} Invalid event type
     */
    emit: function (type) {
      checkEvent(type)

      var args = Array.prototype.slice.call(arguments, 1)
      var list = eventMap[type] || []
      var i = 0

      for (; i < list.length; i++) {
        list[i].apply(undefined, args)
      }
      for (var j = 0; j < subscribeMap.length; j++) {
        subscribeMap[j].call(undefined, type, args)
      }
    },

    /*
     * Add a subscription for all event types.
     *
     * @method subscribe
     * @param {Function} listener The listener for all event types.
     * @throws {Error} Listener not a function
     */
    subscribe: function (listener) {
      if (typeof listener === 'function') {
        subscribeMap.push(listener)
      } else {
        throw new Error('Listener not a function')
      }
    },

    /*
     * Remove a subscription for all event types.
     *
     * @method unsubscribe
     * @param {Function} listener The listener for all event types.
     * @throws {Error} Listener not a function
     */
    unsubscribe: function (listener) {
      if (typeof listener === 'function') {
        var i = subscribeMap.length
        while (i--) {
          if (listener === subscribeMap[i]) {
            subscribeMap.splice(i, 1)
          }
        }
      } else {
        throw new Error('Listener not a function')
      }
    },

    /*
     * Sets the emitter in strict mode where it only allows events that have been defined or aliases.
     *
     * @method setStrictMode
     * @param {Boolean} strict Whether to set strict mode for the emitter.
     */
    setStrictMode: function (strict) {
      strictMode = strict
    }
  })
}
